import { sendPasswordResetEmail } from 'firebase/auth';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../shared/translation/i18n'; // adjust path if needed
import { getAuthTokenFromCustomToken } from '../../../api/auth/signInWithCustomToken';
import { createNewAccount } from '../../../api/users/createNewAccount';
import { View } from '../../../common/constants/ViewEnum';
import { useAccountCreationContext } from '../../../common/context/AccountCreationContext';
import { useAuthContext } from '../../../common/context/AuthContext';
import { auth } from '../../../config/firebaseConfig';
import AnalysisAnimation from '../../animations/AnalysisAnimation';
import PrimaryParagraph from '../../texts/PrimaryParagraph';
import PrimaryText from '../../texts/PrimaryText';
import { AccountCreationViewProps } from '../types';

const MAX_DURATION = 60000;

const AnalysisView: FC<AccountCreationViewProps & {
  modalHeight?: number,
  modalWidth?: number,
  centerImageUrl: string // ✅ new prop
  bestSizeReady?: boolean;
  refreshBestSize?: () => Promise<void>;
  resetAddItemState?: () => void;
}> = ({ centerImageUrl, bestSizeReady = false, refreshBestSize, resetAddItemState, ...props }) => {
  console.debug("[AnalysisView] rendered!");
  const { email: authEmail, uid, signInProvider, persistAuth, setIsOnboarded } = useAuthContext();
  const { dob, shoppingPreference, fashionStyle, items, email: accountCreationEmail, resetAccountCreationState } = useAccountCreationContext();
  const { t } = useTranslation();

  /*const steps = [
    { text: t('create_account_standby'), duration: 2000, start: 0, end: 25 },
    { text: t('adding_item_standby'), duration: 3000, start: 25, end: 50 },
    { text: t('analyzing_profile_standby'), duration: 3000, start: 50, end: 75 },
    { text: t('tailoring_needs_standby'), duration: 1500, start: 75, end: 100 },
    { text: t('logging_standby'), duration: 1000, start: 100, end: 100 },
  ];*/

  const messages = [
  { text: t("creating_profile"), duration: 500, start: 0, end: 25 },
  { text: t("analyzing_profile"), duration: 700, start: 25, end: 50 },
  { text: t("matching_taste"), duration: 300, start: 50, end: 75 },
  { text: t("calculating_perfect_size"), duration: 500, start: 75, end: 90 },
];

  const [accountCreationSuccess, setAccountCreationSuccess] = useState(false);
  const [progress, setProgress] = useState(0);
  const animationFrameId = useRef<number | null>(null);
  const [messageIndex, setMessageIndex] = useState(0);
  const [minDisplayPassed, setMinDisplayPassed] = useState(false);

   // ⏱ ensure at least 2 seconds of display
  useEffect(() => {
    const timer = setTimeout(() => setMinDisplayPassed(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const start = performance.now();

    const animate = (ts: number) => {
      const elapsed = ts - start;
      const pct = Math.min(elapsed / MAX_DURATION, 1) * 100;
      setProgress(pct);
      if (pct < 100) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
  if (messageIndex < messages.length) {
    const { duration, start, end } = messages[messageIndex];
    const startTime = performance.now();

    const animate = (ts: number) => {
      const elapsed = ts - startTime;
      const pct = Math.min(elapsed / duration, 1);
      const value = start + (end - start) * pct;

      setProgress((prev) => Math.max(prev, value));

      if (pct < 1) {
        animationFrameId.current = requestAnimationFrame(animate);
      }
    };

    animationFrameId.current = requestAnimationFrame(animate);

    // Move to next message after its duration
    const timer = setTimeout(() => {
      setMessageIndex((i) => i + 1);
    }, duration);

    return () => {
      if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
      clearTimeout(timer);
    };
  }
}, [messageIndex]);

  // 🎯 When bestSize is ready AND min 2s passed → finish progress then switch
  useEffect(() => {
  if (bestSizeReady && minDisplayPassed) {
    const start = performance.now();
    const duration = 500;
    const from = progress;

    const animate = (ts: number) => {
      const elapsed = ts - start;
      const pct = Math.min(elapsed / duration, 1);
      setProgress(from + (100 - from) * pct);

      if (pct < 1) {
        requestAnimationFrame(animate);
      } else {
        // 🔄 If this is ADD_ITEM flow, refresh bestSize before transitioning
        if (refreshBestSize) {
          console.debug('🔄 [ADD_ITEM] Refreshing bestSize after item addition completion');
          refreshBestSize().then(() => {
            // 🧹 Reset form state after successful ADD_ITEM completion
            console.debug('🧹 [ADD_ITEM] Resetting form state after successful completion');
            if (resetAddItemState) {
              resetAddItemState(); // Use comprehensive reset for ADD_ITEM flow
            } else {
              resetAccountCreationState(); // Fallback to basic reset
            }
            
            if (props.toggleView) {
              props.toggleView(View.SizeRecommendation);
              setIsOnboarded(true);
            }
          }).catch((error) => {
            console.error('🔄 [ADD_ITEM] Failed to refresh bestSize:', error);
            // Still reset state and proceed to size recommendation even if refresh fails
            console.debug('🧹 [ADD_ITEM] Resetting form state after error');
            if (resetAddItemState) {
              resetAddItemState(); // Use comprehensive reset for ADD_ITEM flow
            } else {
              resetAccountCreationState(); // Fallback to basic reset
            }
            
            if (props.toggleView) {
              props.toggleView(View.SizeRecommendation);
              setIsOnboarded(true);
            }
          });
        } else {
          // Regular CREATE_ACCOUNT flow
          if (props.toggleView) {
            props.toggleView(View.SizeRecommendation);
            setIsOnboarded(true);
          }
        }
      }
    };

    requestAnimationFrame(animate);
  }
}, [bestSizeReady, minDisplayPassed, refreshBestSize]);


/*const animateProgress = (step: { text: string; duration: number; start: number; end: number }) => {
  let startTime: number;
  let currentProgress: number = step.start;

  const updateProgress = (timestamp: number) => {
    if (!startTime) startTime = timestamp;
    const elapsedTime = timestamp - startTime;
    currentProgress = step.start + (elapsedTime / step.duration) * (step.end - step.start);

    if (currentProgress < step.end) {
      setProgress(currentProgress);
      animationFrameId.current = requestAnimationFrame(updateProgress);
    } else {
      setProgress(step.end);
      cancelAnimationFrame(animationFrameId.current!);
    }
  };

  animationFrameId.current = requestAnimationFrame(updateProgress);
};*/

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // If bestSizeReady is true (ADD_ITEM flow), skip account creation
    if (bestSizeReady) {
      console.debug('🔄 [ADD_ITEM] Skipping account creation, items already added');
      setAccountCreationSuccess(true);
      return;
    }

    const handleNewAccountCreation = async () => {
      const email = signInProvider ? authEmail : accountCreationEmail;
      console.debug('🔧 [ANALYSIS] Starting account creation with:', {
        email: email || '(empty)',
        hasItems: items.length > 0,
        signInProvider,
        dob,
        shoppingPreference
      });
      
      try {
        const createAccountResponse = await createNewAccount({
          dob,
          shoppingPreference,
          fashionStyle,
          items,
          email,
          uid,
        });

        console.debug('✅ [ANALYSIS] Account creation response:', createAccountResponse);

				if (createAccountResponse.status_code === 201) {
					if (!signInProvider) {
						const getAuthTokenFromCustomTokenResponse = await getAuthTokenFromCustomToken(createAccountResponse.custom_token);
            auth.languageCode = i18n.language || 'en';
            
            if (email){
              await sendPasswordResetEmail(auth, email);
            }
            persistAuth(getAuthTokenFromCustomTokenResponse.token);
          }
          setAccountCreationSuccess(true);
        } else {
          console.error('❌ [ANALYSIS] Account creation failed with status:', createAccountResponse.status_code);
        }
      } catch (error: any) {
        console.error('❌ [ANALYSIS] Account creation error:', error);
        console.debug(error);
        // TODO: if user creation fails, we should still display a size recommendation! or an error message...
        // displaying a size reco is hard because user is not created...
      }
    };

    handleNewAccountCreation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authEmail, bestSizeReady]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
    if (accountCreationSuccess && props.toggleView) {
      props.toggleView(View.SizeRecommendation);
      setIsOnboarded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountCreationSuccess]);

    /*// eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
    if (currentStep < steps.length) {
      const step = steps[currentStep];
      animateProgress(step);

      setTimeout(() => {
        setCurrentStep((prev) => prev + 1);
      }, step.duration + 500);
    }

    return () => {
      if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
    };
  }, [currentStep]);*/

  return (
    <div className="pop-flex pop-flex-col pop-justify-around pop-items-center pop-h-full pop-w-full pop-fadeIn">
      <AnalysisAnimation centerImageUrl={centerImageUrl} />

      <div className="pop-flex pop-flex-col pop-items-center pop-w-full pop-px-4 pop-pb-4 pop-gap-2">
        <PrimaryText className="pop-text-center">{t("perfect_size_standby")}</PrimaryText>

        {/* Progress bar */}
        <div className="pop-w-full pop-bg-popsizeGradientPrimary10Neutral10 pop-rounded-full pop-h-2.5">
          <div
            className="pop-bg-popsizeGradientSecondary9050 pop-h-2.5 pop-rounded-full"
            style={{ width: `${progress}%`, transition: "width 0.1s ease-out" }}
          />
        </div>

        {/* Current message */}
        <PrimaryParagraph className="pop-text-center pop-mt-4">
          {messages[messageIndex]?.text}
        </PrimaryParagraph>
      </div>
    </div>
  );
};


export default AnalysisView;
